import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/CopilotAssignmentModal'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {fuzzyFilter} from '@github-ui/fuzzy-score/fuzzy-filter'
import {hasFuzzyMatch} from '@github-ui/fuzzy-score/has-fuzzy-match'
import {ItemPickerAssigneeBase} from '@github-ui/item-picker-assignee'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import type {ExtendedItemProps, SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
import type React from 'react'
import {type RefObject, useCallback, useEffect, useMemo, useState} from 'react'
import {type Environment, fetchQuery, graphql, readInlineData, useRelayEnvironment} from 'react-relay'

import type {
  AssigneePickerSearchAssignableRepositoryUsersSharedQuery,
  AssigneePickerSearchAssignableRepositoryUsersSharedQuery$data,
  RepositorySuggestedActorFilter,
} from './__generated__/AssigneePickerSearchAssignableRepositoryUsersSharedQuery.graphql'
import type {
  AssigneePickerSearchAssignableUsersSharedQuery,
  AssigneePickerSearchAssignableUsersSharedQuery$data,
} from './__generated__/AssigneePickerSearchAssignableUsersSharedQuery.graphql'
import type {
  AssigneePickerSharedAssignee$data,
  AssigneePickerSharedAssignee$key,
} from './__generated__/AssigneePickerSharedAssignee.graphql'
import {useViewer} from './hooks/useViewer'
import type {updateIssueAssigneesBulkByQueryMutationSharedMutation$data} from './mutations/__generated__/updateIssueAssigneesBulkByQueryMutationSharedMutation.graphql'
import type {updateIssueAssigneesBulkMutationSharedMutation$data} from './mutations/__generated__/updateIssueAssigneesBulkMutationSharedMutation.graphql'
import {commitUpdateIssueAssigneesBulkByQueryMutationSharedMutation} from './mutations/update-issue-assignees-bulk-by-query-mutation'
import {commitUpdateIssueAssigneesBulkMutationSharedMutation} from './mutations/update-issue-assignees-bulk-mutation'
import {getActorCapabilities} from './utils/assignee-picker'

export type AssigneePickerAssignee = Omit<AssigneePickerSharedAssignee$data, ' $fragmentType'> & {type?: string}

export type AssigneePickerSearchAssignableRepositoryUsersQuery =
  AssigneePickerSearchAssignableRepositoryUsersSharedQuery

type Name = 'assignee' | 'author'

export type AssigneePickerProps = {
  repo: string
  owner: string
  readonly: boolean
  /** An optional data set to be used when the picker is first opened. If this data is not provided, a loading state
   * is shown in the picker and the data is loaded on demand.
   */
  suggestions?: AssigneePickerAssignee[]
  assignees: AssigneePickerAssignee[]
  insidePortal?: boolean
  shortcutEnabled: boolean
  noAssigneeOption?: ExtendedItemProps<AssigneePickerAssignee>
  selectionVariant?: 'single' | 'multiple'
  anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref: RefObject<HTMLButtonElement>) => React.ReactElement
  onSelectionChange: (selectedAssignees: AssigneePickerAssignee[]) => void
  /**
   * Whether to render the assignee picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  title?: string
  name?: Name
  maximumAssignees?: number
  showNoMatchItem?: boolean
}

export function readAssigneeFromFragment(key: AssigneePickerSharedAssignee$key): AssigneePickerAssignee {
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData(AssigneeFragment, key)
}

export async function fetchSearchAssignees(
  environment: Environment,
  options: {
    owner: string
    repo: string
    assignees: string[]
    capabilities: RepositorySuggestedActorFilter[]
  },
): Promise<AssigneePickerAssignee[]> {
  const {owner, repo, assignees, capabilities} = options

  const data = await clientSideRelayFetchQueryRetained<AssigneePickerSearchAssignableRepositoryUsersSharedQuery>({
    environment,
    query: SearchAssignableRepositoryUsers,
    variables: {
      owner,
      name: repo,
      loginNames: assignees.join(',') ?? [],
      first: assignees.length ?? 0,
      capabilities,
    },
  }).toPromise()

  if (!data?.repository?.suggestedActors?.ok) {
    return []
  }
  return (data?.repository?.suggestedActors?.value.nodes ?? [])
    .filter(node => !!node)
    .map(node => readAssigneeFromFragment(node))
}

export type AssigneeIssuePickerProps = AssigneePickerProps & {
  number: number
}

export type AssigneeRepositoryPickerProps = AssigneePickerProps & {
  assigneeTokens: string[]
  includeAssignableBots: boolean
  includeAuthorableBots: boolean
}

type BulkAssigneePickerProps = Omit<AssigneePickerProps, 'id' | 'assignees' | 'onSelectionChange'> & {
  assigneesAppliedToAll: AssigneePickerAssignee[]
  assigneesAppliedToSome: AssigneePickerAssignee[]
  existingIssueAssignees: Map<string, string[]>
  connectionIds: {[key: string]: string[]}
  issueNumbers: number[]
  repository?: {
    databaseId: number | null
    isPrivate: boolean
    isArchived: boolean
  }
} & SharedBulkActionsItemPickerProps

// note: when the query variable is empty, this the suggestedAssignees resolvers is returning the participants
export const SearchAssignableUsersQuery = graphql`
  query AssigneePickerSearchAssignableUsersSharedQuery(
    $owner: String!
    $name: String!
    $number: Int!
    $query: String!
    $first: Int!
  ) {
    repository(owner: $owner, name: $name) {
      issueOrPullRequest(number: $number) {
        ... on Assignable {
          suggestedActors(first: $first, query: $query) @catch(to: RESULT) {
            nodes @required(action: THROW) {
              ...AssigneePickerSharedAssignee @dangerously_unaliased_fixme
            }
          }
        }
      }
    }
  }
`

export const SearchAssignableRepositoryUsers = graphql`
  query AssigneePickerSearchAssignableRepositoryUsersSharedQuery(
    $owner: String!
    $name: String!
    $query: String
    $loginNames: String
    $first: Int!
    $capabilities: [RepositorySuggestedActorFilter!]!
  ) {
    repository(owner: $owner, name: $name) {
      suggestedActors(first: $first, query: $query, loginNames: $loginNames, capabilities: $capabilities)
        @catch(to: RESULT) {
        nodes @required(action: THROW) {
          ...AssigneePickerSharedAssignee
        }
        totalCount
      }
    }
  }
`

const AssigneeFragment = graphql`
  fragment AssigneePickerSharedAssignee on Actor @inline {
    __typename
    id
    login
    name
    # eslint-disable-next-line relay/unused-fields
    profileResourcePath
    # eslint-disable-next-line relay/unused-fields
    avatarUrl(size: 64)
    ... on Bot {
      # This fragment is used in several places. Despite this field not being used here, we rely on it in other files.
      # eslint-disable-next-line relay/unused-fields
      isCopilot
    }
  }
`

export type AssigneePickerSharedAssigneeKey = AssigneePickerSharedAssignee$key

export function AssigneePicker({anchorElement, shortcutEnabled, ...props}: AssigneeIssuePickerProps) {
  return (
    <LazyItemPicker
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <AssigneePickerInternal
          anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
          shortcutEnabled={shortcutEnabled}
          triggerOpen
          {...props}
        />
      )}
      insidePortal={props.insidePortal}
      keybindingCommandId={getKeyBindingCommandId(props.name)}
    />
  )
}

export function AssigneeRepositoryPicker({shortcutEnabled, anchorElement, ...props}: AssigneeRepositoryPickerProps) {
  return (
    <LazyItemPicker
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      keybindingCommandId={getKeyBindingCommandId(props.name)}
      createChild={() => (
        <AssigneeRepositoryPickerInternal
          shortcutEnabled={shortcutEnabled}
          anchorElement={anchorElement}
          triggerOpen
          {...props}
        />
      )}
    />
  )
}

function AssigneeRepositoryPickerInternal({
  repo,
  owner,
  assignees,
  assigneeTokens,
  suggestions,
  maximumAssignees,
  includeAssignableBots,
  includeAuthorableBots,
  ...props
}: AssigneeRepositoryPickerProps & {assigneeTokens: string[]}) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<AssigneePickerSearchAssignableRepositoryUsersSharedQuery$data | null>(null)
  const [suggestedAssignees, setSuggestedAssignees] = useState<AssigneePickerAssignee[] | null>(null)
  const [totalAssignableUsers, setTotalAssignableUsers] = useState<number | null>(null)

  const capabilities = useMemo(
    () => getActorCapabilities({includeAssignableBots, includeAuthorableBots}),
    [includeAssignableBots, includeAuthorableBots],
  )

  const shouldFetchFromAssigneeTokens = assigneeTokens.length > 0
  useEffect(() => {
    if (!IS_SERVER && shouldFetchFromAssigneeTokens) {
      setIsLoading(true)
      clientSideRelayFetchQueryRetained<AssigneePickerSearchAssignableRepositoryUsersSharedQuery>({
        environment,
        query: SearchAssignableRepositoryUsers,
        variables: {
          owner,
          name: repo,
          loginNames: assigneeTokens.join(','),
          first: assigneeTokens.length,
          capabilities,
        },
      }).subscribe({
        next: internalData => {
          setData(internalData ?? null)
          setIsLoading(false)
        },
        error: () => {
          setIsLoading(false)
        },
      })
    }
  }, [
    environment,
    owner,
    repo,
    assigneeTokens,
    includeAssignableBots,
    includeAuthorableBots,
    capabilities,
    shouldFetchFromAssigneeTokens,
  ])

  const searchAssignees = useCallback(
    (query: string) => {
      if (totalAssignableUsers && totalAssignableUsers <= VALUES.maximumSuggestedUsers && suggestedAssignees) {
        setIsLoading(false)
        return fuzzyFilter<AssigneePickerAssignee>({
          items: [
            ...suggestedAssignees,
            ...(props.noAssigneeOption ? [SPECIAL_VALUES.noAssigneeData as AssigneePickerAssignee] : []),
          ],
          filter: query,
          key: (l: AssigneePickerAssignee) => l.login || (props.noAssigneeOption?.source?.login ?? ''),
          secondaryKey: (l: AssigneePickerAssignee) => `${l.name}`,
        })
      }

      return new Promise<AssigneePickerAssignee[]>((resolve, reject) => {
        setIsLoading(true)
        fetchQuery<AssigneePickerSearchAssignableRepositoryUsersSharedQuery>(
          environment,
          SearchAssignableRepositoryUsers,
          {
            owner,
            name: repo,
            query,
            first: VALUES.maximumSuggestedUsers,
            capabilities,
          },
        ).subscribe({
          next: (response: AssigneePickerSearchAssignableRepositoryUsersSharedQuery$data) => {
            if (response !== null && response.repository?.suggestedActors?.ok) {
              const assignableActors: AssigneePickerAssignee[] = (
                response.repository?.suggestedActors?.value.nodes || []
              ).flatMap(actor =>
                actor
                  ? // eslint-disable-next-line no-restricted-syntax
                    [readInlineData<AssigneePickerSharedAssignee$key>(AssigneeFragment, actor)]
                  : [],
              )
              // The total count represents the total number of assignable users in the repository ONLY when the query is empty.
              if (response.repository?.suggestedActors?.value.totalCount && query.trim() === '') {
                setTotalAssignableUsers(response.repository.suggestedActors.value.totalCount)
              }

              if (props.noAssigneeOption && hasFuzzyMatch(query, props.noAssigneeOption?.source?.login ?? '')) {
                assignableActors.unshift({
                  ...SPECIAL_VALUES.noAssigneeData,
                } as AssigneePickerAssignee)
              }

              setIsLoading(false)
              resolve(assignableActors)
            } else {
              reject(new Error(ERRORS.couldNotSearchAssignees))
            }
          },
          error: (error: Error) => {
            reject(error)
            setIsLoading(false)
          },
        })
      })
    },
    [totalAssignableUsers, suggestedAssignees, props.noAssigneeOption, environment, owner, repo, capabilities],
  )

  const suggestedActors: AssigneePickerAssignee[] = useMemo(() => {
    let all: AssigneePickerAssignee[] = []
    if (assignees.length > 0) {
      all = assignees
    } else if (assigneeTokens.length > 0) {
      all = ((data?.repository?.suggestedActors?.ok && data?.repository?.suggestedActors?.value.nodes) || []).flatMap(
        actor =>
          actor
            ? // eslint-disable-next-line no-restricted-syntax
              [readInlineData<AssigneePickerSharedAssignee$key>(AssigneeFragment, actor)]
            : [],
      )
    }

    if (props.noAssigneeOption?.selected) {
      all.unshift(SPECIAL_VALUES.noAssigneeData as AssigneePickerAssignee)
    }

    return all
  }, [assigneeTokens.length, assignees, data?.repository?.suggestedActors, props.noAssigneeOption?.selected])

  useEffect(() => {
    const fetchSuggestedAssignees = async () => {
      const result = await searchAssignees('')
      setSuggestedAssignees(result)
      setIsLoading(false)
    }

    fetchSuggestedAssignees()
    // disabling this as we only want to fetch the suggested assignees once
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const suggestedItems = useMemo(() => {
    const copilot = (suggestedAssignees || []).find(a => a.isCopilot)
    // create a new array instance because if we need to insert copilot
    // we need a new instance to force a re-render of the child component (ItemPickerAssigneeBase)
    const source = [...(suggestions ? suggestions : suggestedAssignees || [])]

    if (copilot && !source.find(a => a.isCopilot)) {
      source.unshift(copilot)
    }

    return source
  }, [suggestedAssignees, suggestions])

  const viewer = useViewer()

  return (
    <ItemPickerAssigneeBase
      {...props}
      assignees={suggestedActors}
      suggestions={suggestedItems}
      repo={repo}
      owner={owner}
      isLoading={isLoading}
      searchAssignees={searchAssignees}
      maximumAssignees={maximumAssignees}
      viewer={viewer}
    />
  )
}

function AssigneePickerInternal({
  number,
  owner,
  repo,
  assignees,
  suggestions,
  maximumAssignees,
  ...props
}: AssigneeIssuePickerProps) {
  const environment = useRelayEnvironment()
  const hasPreloadedData = (suggestions?.length || 0) > 0
  const [isLoading, setIsLoading] = useState(!hasPreloadedData)
  const [data, setData] = useState<AssigneePickerSearchAssignableUsersSharedQuery$data | null>(null)

  useEffect(() => {
    if (!IS_SERVER && !hasPreloadedData) {
      clientSideRelayFetchQueryRetained<AssigneePickerSearchAssignableUsersSharedQuery>({
        environment,
        query: SearchAssignableUsersQuery,
        variables: {owner, name: repo, number, query: '', first: 10},
      }).subscribe({
        next: internalData => {
          setData(internalData ?? null)
          setIsLoading(false)
        },
        error: () => {
          setIsLoading(false)
        },
      })
    }
  }, [environment, owner, repo, number, hasPreloadedData])

  const suggestedActors = suggestions
    ? suggestions
    : (
        (data?.repository?.issueOrPullRequest?.suggestedActors?.ok &&
          data?.repository?.issueOrPullRequest?.suggestedActors?.value.nodes) ||
        []
      ).flatMap(actor =>
        actor
          ? [
              // eslint-disable-next-line no-restricted-syntax
              readInlineData<AssigneePickerSharedAssignee$key>(AssigneeFragment, actor),
            ]
          : [],
      )

  const searchAssignees = useCallback(
    (query: string) => {
      return new Promise<AssigneePickerAssignee[]>((resolve, reject) => {
        fetchQuery<AssigneePickerSearchAssignableUsersSharedQuery>(environment, SearchAssignableUsersQuery, {
          owner,
          name: repo,
          number,
          first: 10,
          query,
        }).subscribe({
          next: (response: AssigneePickerSearchAssignableUsersSharedQuery$data) => {
            if (response !== null && response.repository?.issueOrPullRequest?.suggestedActors?.ok) {
              const assignableActorNodes = (
                response.repository?.issueOrPullRequest?.suggestedActors?.value.nodes || []
              ).flatMap(
                // eslint-disable-next-line no-restricted-syntax
                node => (node ? [readInlineData<AssigneePickerSharedAssignee$key>(AssigneeFragment, node)] : []),
              )

              resolve(assignableActorNodes)
            } else {
              reject(new Error(ERRORS.couldNotSearchAssignees))
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      })
    },
    [environment, number, owner, repo],
  )

  const viewer = useViewer()

  return (
    <ItemPickerAssigneeBase
      {...props}
      repo={repo}
      owner={owner}
      isLoading={isLoading}
      assignees={assignees}
      suggestions={suggestedActors}
      searchAssignees={searchAssignees}
      maximumAssignees={maximumAssignees}
      viewer={viewer}
    />
  )
}

export function BulkIssuesAssigneePicker({
  useQueryForAction,
  repositoryId,
  query,
  issuesToActOn,
  assigneesAppliedToAll,
  assigneesAppliedToSome,
  existingIssueAssignees,
  connectionIds,
  onError,
  onCompleted,
  repository,
  issueNumbers,
  ...rest
}: BulkAssigneePickerProps) {
  const environment = useRelayEnvironment()
  const [copilotModalOpen, setCopilotModalOpen] = useState(false)
  const [pickerInstanceKey, setPickerInstanceKey] = useState(0)

  const crossRepoAssignEnabled = isFeatureEnabled('issues_copilot_cross_repo_assign')

  const assigneesIncludeCopilot = useMemo(() => {
    return assigneesAppliedToAll.some(assignee => assignee.isCopilot)
  }, [assigneesAppliedToAll])

  const canUseCopilotAssignment = crossRepoAssignEnabled && !assigneesIncludeCopilot

  const issueNumbersWithoutCopilot = useMemo(() => {
    const copilotId = [...assigneesAppliedToAll, ...assigneesAppliedToSome].find(a => a.isCopilot)?.id

    if (!copilotId) return [...issueNumbers]

    return issuesToActOn.reduce((acc: number[], issueId, idx) => {
      const assignees = existingIssueAssignees.get(issueId) || []
      const hasCopilot = assignees.some(id => id === copilotId)
      const numberAtIndex = issueNumbers[idx]
      if (!hasCopilot && typeof numberAtIndex === 'number') acc.push(numberAtIndex)
      return acc
    }, [])
  }, [assigneesAppliedToAll, assigneesAppliedToSome, existingIssueAssignees, issueNumbers, issuesToActOn])

  const executeBulkAssigneeMutation = useCallback(
    (assigneesToProcess: AssigneePickerAssignee[]) => {
      const assigneesToAdd = assigneesToProcess.filter(a => !assigneesAppliedToAll.some(aa => aa.id === a.id))
      const assigneesToRemove = assigneesAppliedToAll.filter(a => !assigneesToProcess.some(aa => aa.id === a.id))

      if (!assigneesToAdd.length && !assigneesToRemove.length) {
        return
      }

      const args = {
        applyAssigneeIds: assigneesToAdd.map(l => l.id),
        removeAssigneeIds: assigneesToRemove.map(l => l.id),
      }

      const mutationArgs = {
        environment,
        optimisticUpdateIds: issuesToActOn,
        existingIssueAssignees,
        connectionIds,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueAssigneesBulkByQueryMutationSharedMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            ...args,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueAssigneesBulkByQueryMutationSharedMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueAssigneesBulkMutationSharedMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            ...args,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueAssigneesBulkMutationSharedMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [
      assigneesAppliedToAll,
      connectionIds,
      environment,
      existingIssueAssignees,
      issuesToActOn,
      onCompleted,
      onError,
      query,
      repositoryId,
      useQueryForAction,
    ],
  )

  const handleCopilotModalOpen = useCallback(() => {
    setCopilotModalOpen(true)
    /* Force a remount of the picker so it resets to initialSelectedItems (equivalent to Cancel)
      This is needed because when we do nothing with Copilot, it remains selected in the picker
      and the modal would reopen immediately when the picker opens */
    setPickerInstanceKey(prev => prev + 1)
  }, [])

  const onSelectionChange = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      const copilotAssignee = selectedAssignees.find(assignee => assignee.isCopilot)
      const nonCopilotAssignees = selectedAssignees.filter(assignee => !assignee.isCopilot)

      if (copilotAssignee && canUseCopilotAssignment) {
        // open modal and process non-copilot changes in the background
        handleCopilotModalOpen()
        executeBulkAssigneeMutation(nonCopilotAssignees)
      } else {
        executeBulkAssigneeMutation(selectedAssignees)
      }
    },
    [executeBulkAssigneeMutation, canUseCopilotAssignment, handleCopilotModalOpen],
  )

  const showModal = canUseCopilotAssignment && copilotModalOpen

  const repositoryProps = useMemo(
    () => ({
      databaseId: repository?.databaseId ?? null,
      isPrivate: repository?.isPrivate ?? false,
      isArchived: repository?.isArchived ?? false,
      name: rest.repo,
      nameWithOwner: `${rest.owner}/${rest.repo}`,
      owner: {
        login: rest.owner,
      },
    }),
    [repository, rest.repo, rest.owner],
  )

  return (
    <>
      <AssigneeRepositoryPicker
        key={`bulk-assignee-picker-${pickerInstanceKey}`}
        {...rest}
        assignees={assigneesAppliedToAll}
        assigneeTokens={[]}
        onSelectionChange={onSelectionChange}
        includeAssignableBots
        includeAuthorableBots={false}
      />
      {showModal && issueNumbersWithoutCopilot.length > 0 && (
        <CopilotAssignmentModal
          isOpen={copilotModalOpen}
          onClose={() => setCopilotModalOpen(false)}
          repository={repositoryProps}
          issueNumbers={issueNumbersWithoutCopilot}
          onAssignmentComplete={() => {
            setCopilotModalOpen(false)
            onCompleted?.()
          }}
        />
      )}
    </>
  )
}

const getKeyBindingCommandId = (name?: string) =>
  name === 'author' ? 'item-pickers:open-author' : 'item-pickers:open-assignees'

try{ AssigneePicker.displayName ||= 'AssigneePicker' } catch {}
try{ AssigneeRepositoryPicker.displayName ||= 'AssigneeRepositoryPicker' } catch {}
try{ AssigneeRepositoryPickerInternal.displayName ||= 'AssigneeRepositoryPickerInternal' } catch {}
try{ AssigneePickerInternal.displayName ||= 'AssigneePickerInternal' } catch {}
try{ BulkIssuesAssigneePicker.displayName ||= 'BulkIssuesAssigneePicker' } catch {}